import {
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
} from "@mui/icons-material";
import { FC, useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { ScheduleTableDataRow_schedule$key } from "~/src/__relay_artifacts__/ScheduleTableDataRow_schedule.graphql";
import {
  TableDataColumn,
  TableDataRow,
} from "~/src/components/common/tables/Table";
import { TableActions } from "~/src/components/common/tables/TableActions";
import { useOpenState } from "~/src/lib/hooks";
import { formatISOToTime } from "~/src/lib/utils/date-utils";

import { DeleteConfirmDialog } from "../../../global/DeleteConfirmDialog";

export type Props = {
  scheduleRef: ScheduleTableDataRow_schedule$key;
  siteSlug: string;
  connectionId: string;
  onDeleteClick: (scheduleId: string, connectionId: string) => void;
};

const scheduleFragment = graphql`
  fragment ScheduleTableDataRow_schedule on Schedule {
    id
    slug
    startTime
    endTime
    createdAt
    updatedAt
  }
`;

export const ScheduleTableDataRow: FC<Props> = ({
  scheduleRef,
  siteSlug,
  connectionId,
  onDeleteClick,
}) => {
  const { isOpen, onOpen, onClose } = useOpenState();
  const schedule = useFragment(scheduleFragment, scheduleRef);
  const handleDeleteClick = useCallback(() => {
    onOpen();
  }, [onOpen]);
  const handleDeleteConfirm = useCallback(() => {
    onDeleteClick(schedule.id, connectionId);
    onClose();
  }, [onDeleteClick, schedule.id, connectionId, onClose]);

  const editTo = useMemo(() => {
    return siteSlug
      ? `/sites/${siteSlug}/schedules/${schedule.slug}/edit`
      : undefined;
  }, [schedule.slug, siteSlug]);

  const actionMenus = useMemo(
    () => [
      {
        label: "編集",
        icon: <EditOutlinedIcon />,
        to: editTo,
      },
      {
        label: "削除",
        icon: <DeleteOutlineIcon />,
        onClick: handleDeleteClick,
      },
    ],
    [editTo, handleDeleteClick]
  );

  const startTime = formatISOToTime(schedule.startTime);
  const endTime = formatISOToTime(schedule.endTime);

  return (
    <>
      <TableDataRow key={schedule.id}>
        <TableDataColumn type="text" data={startTime} />
        <TableDataColumn type="text" data={endTime} />
        <TableDataColumn
          alignItems="center"
          type="date"
          data={schedule.createdAt}
        />
        <TableDataColumn type="date" data={schedule.updatedAt} />
        <TableDataColumn
          type="actions"
          data={<TableActions actionMenus={actionMenus} />}
        />
      </TableDataRow>
      {isOpen && (
        <DeleteConfirmDialog
          name={`${startTime}〜${endTime}`}
          isOpen={isOpen}
          onClose={onClose}
          onDelete={handleDeleteConfirm}
        />
      )}
    </>
  );
};
